import { StandardError } from "@core-ts/StandardError"
import { castAsLong } from "./helperLibrary"
import Long from "long"
import { TimeStamp } from "./TimeStamp"
import { DataType, NativeValueType } from "./clients/StorageServiceClient"
import { ResultRowVariant, ResultRowVariantClassless } from "./clients/StorageServiceClient"
import { BinaryRep } from "./BinaryRep"

export class Variant {
    private type: DataType
    private stringValue: string | null
    private numberValue: number | null
    private timeStampValue: TimeStamp | null
    private binaryValue: string | null
    value: any

    public constructor(type: DataType, value: string | number | TimeStamp | BinaryRep | null) {
        this.type = type

        this.stringValue = null
        this.numberValue = null
        this.timeStampValue = null
        this.binaryValue = null

        if (type === DataType.Double) {
            if (value !== null && typeof value !== "number")
                throw new StandardError("Variant::constructor: DataType.Double must be number")
            this.numberValue = value
        } else if (type === DataType.ID) {
            if (value !== null && typeof value !== "number")
                throw new StandardError("Variant::constructor: DataType.ID must be number")
            this.numberValue = value
        } else if (type === DataType.Integer) {
            if (value !== null && typeof value !== "number")
                throw new StandardError("Variant::constructor: DataType.Integer must be number")
            this.numberValue = value
        } else if (type === DataType.Path) {
            let stringValue = value
            if (typeof value === "number") stringValue = value.toString()

            if (stringValue !== null && typeof stringValue !== "string")
                throw new StandardError("Variant::constructor: DataType.Path must be number or string")
            this.stringValue = stringValue
        } else if (type === DataType.String) {
            if (value !== null && typeof value !== "string")
                throw new StandardError("Variant::constructor: DataType.String must be string")
            this.stringValue = value
        } else if (type === DataType.TimeStamp) {
            if (typeof value === "number") {
                this.timeStampValue = TimeStamp.fromUnixTimestamp(value)
            } else if (typeof value === "object" && value instanceof TimeStamp) {
                this.timeStampValue = value
            } else if (value === null) {
                this.timeStampValue = null
            } else
                throw new StandardError("Variant::constructor: DataType.TimeStamp must be number or TimeStamp object")
        } else if (type === DataType.Folder) {
            if (value !== null && typeof value !== "string")
                throw new StandardError("Variant::constructor: DataType.Folder must be string")
            this.stringValue = value
        } else if (type === DataType.Binary) {
            const objBinVal = typeof value === "string" ? JSON.parse(value) : value

            if (objBinVal === null) {
                this.binaryValue = null
            } else if (BinaryRep.isBinary(objBinVal)) {
                this.binaryValue = JSON.stringify(objBinVal)
            } else {
                throw new StandardError("Variant::constructor: DataType.Binary must be Binary")
            }
        } else throw new StandardError("Variant::constructor: Unknown data type")
    }

    static fromID(idValue: number | null): Variant {
        return new Variant(DataType.ID, idValue)
    }

    static fromPath(pathValue: string | null): Variant {
        return new Variant(DataType.Path, pathValue)
    }

    static fromString(stringValue: string | null): Variant {
        return new Variant(DataType.String, stringValue)
    }

    static fromInteger(integerValue: number | null): Variant {
        return new Variant(DataType.Integer, integerValue)
    }

    static fromDouble(doubleValue: number | null): Variant {
        return new Variant(DataType.Double, doubleValue)
    }

    static fromTimeStamp(timeStampValue: TimeStamp | null): Variant {
        return new Variant(DataType.TimeStamp, timeStampValue)
    }

    static fromFolder(stringValue: string | null): Variant {
        return new Variant(DataType.Folder, stringValue)
    }

    static fromBinary(binaryValue: BinaryRep | null): Variant {
        return new Variant(DataType.Binary, binaryValue)
    }

    static fromNativeType(nativeValue: NativeValueType, fieldType: DataType): Variant {
        switch (fieldType) {
            case DataType.ID: {
                if (nativeValue !== null && typeof nativeValue !== "number")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.ID, nativeValue)
            }
            case DataType.Path: {
                if (nativeValue !== null && typeof nativeValue !== "string")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.Path, nativeValue)
            }
            case DataType.String: {
                if (nativeValue !== null && typeof nativeValue !== "string")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.String, nativeValue)
            }
            case DataType.Integer: {
                if (nativeValue !== null && typeof nativeValue !== "number")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.Integer, nativeValue)
            }
            case DataType.Double: {
                if (nativeValue !== null && typeof nativeValue !== "number")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.Double, nativeValue)
            }
            case DataType.TimeStamp: {
                if (nativeValue === null) return new Variant(DataType.TimeStamp, null)

                if (typeof nativeValue !== "number" && !Long.isLong(nativeValue))
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.TimeStamp, TimeStamp.fromLongUnixTimestamp(castAsLong(nativeValue)))
            }
            case DataType.Folder: {
                if (nativeValue !== null && typeof nativeValue !== "string")
                    throw new StandardError("Native does not match field meta data")

                return new Variant(DataType.Folder, nativeValue)
            }
            case DataType.Binary: {
                if (nativeValue === null) return new Variant(DataType.Binary, null)

                if (typeof nativeValue !== "string") {
                    throw new StandardError("Native does not match field meta data(1)")
                }

                if (!BinaryRep.isBinaryJSON(nativeValue)) {
                    throw new StandardError("Native does not match field meta data(2)")
                }

                return new Variant(DataType.Binary, nativeValue)
            }
            default:
                throw new StandardError("Invalid type passed to Variant.fromNativeType")
        }
    }

    toNativeType(): NativeValueType {
        switch (this.type) {
            case DataType.ID:
                return this.numberValue
            case DataType.Path:
                return this.stringValue
            case DataType.String:
                return this.stringValue
            case DataType.Integer:
                return this.numberValue
            case DataType.Double:
                return this.numberValue
            case DataType.TimeStamp:
                if (this.timeStampValue === null) return null

                return this.timeStampValue.toNativeTimeStamp()
            case DataType.Folder:
                return this.stringValue
            case DataType.Binary:
                return this.binaryValue

            default:
                throw new StandardError("Invalid type passed to Variant.toNativeType")
        }
    }

    public getType(): DataType {
        return this.type
    }

    public isNull(): boolean {
        if (this.stringValue === null && this.numberValue === null && this.timeStampValue === null) return true
        return false
    }

    // The nice thing about using switchOnType and switchOnTypeWithNull is that if we add a new type and all the code uses
    // these calls, we will get a compilation error on all places where the new type isn't supported
    public switchOnType<X>(
        idFunc: (x: number | null) => X,
        pathFunc: (x: string | null) => X,
        stringFunc: (x: string | null) => X,
        integerFunc: (x: number | null) => X,
        doubleFunc: (x: number | null) => X,
        timeStampFunc: (x: TimeStamp | null) => X,
        folderFunc: (x: string | null) => X,
        binaryFunc: (x: string | null) => X,
    ): X {
        switch (this.type) {
            case DataType.ID:
                return idFunc(this.numberValue)
            case DataType.Path:
                return pathFunc(this.stringValue)
            case DataType.String:
                return stringFunc(this.stringValue)
            case DataType.Integer:
                return integerFunc(this.numberValue)
            case DataType.Double:
                return doubleFunc(this.numberValue)
            case DataType.TimeStamp:
                return timeStampFunc(this.timeStampValue)
            case DataType.Folder:
                return folderFunc(this.stringValue)
            case DataType.Binary:
                return binaryFunc(this.binaryValue)
            default:
                throw new StandardError("Invalid type passed to Variant.switchOnType")
        }
    }

    public switchOnTypeWithNull<X>(
        nullFunc: () => X,
        idFunc: (x: number) => X,
        pathFunc: (x: string) => X,
        stringFunc: (x: string) => X,
        integerFunc: (x: number) => X,
        doubleFunc: (x: number) => X,
        timeStampFunc: (x: TimeStamp) => X,
        folderFunc: (x: string) => X,
        binaryFunc: (x: string) => X,
    ): X {
        switch (this.type) {
            case DataType.ID:
                if (this.numberValue === null) return nullFunc()
                return idFunc(this.numberValue)
            case DataType.Path:
                if (this.stringValue === null) return nullFunc()
                return pathFunc(this.stringValue)
            case DataType.String:
                if (this.stringValue === null) return nullFunc()
                return stringFunc(this.stringValue)
            case DataType.Integer:
                if (this.numberValue === null) return nullFunc()
                return integerFunc(this.numberValue)
            case DataType.Double:
                if (this.numberValue === null) return nullFunc()
                return doubleFunc(this.numberValue)
            case DataType.TimeStamp:
                if (this.timeStampValue === null) return nullFunc()
                return timeStampFunc(this.timeStampValue)
            case DataType.Folder:
                if (this.stringValue === null) return nullFunc()
                return folderFunc(this.stringValue)
            case DataType.Binary:
                if (this.binaryValue === null) return nullFunc()
                return binaryFunc(this.binaryValue)
            default:
                throw new StandardError("Invalid type passed to Variant.switchOnTypeWithNull")
        }
    }

    public async switchOnTypeWithNullAsync<X>(
        nullFunc: () => Promise<X>,
        idFunc: (x: number) => Promise<X>,
        pathFunc: (x: string) => Promise<X>,
        stringFunc: (x: string) => Promise<X>,
        integerFunc: (x: number) => Promise<X>,
        doubleFunc: (x: number) => Promise<X>,
        timeStampFunc: (x: TimeStamp) => Promise<X>,
        folderFunc: (x: string) => Promise<X>,
    ): Promise<X> {
        switch (this.type) {
            case DataType.ID:
                if (this.numberValue === null) return await nullFunc()
                return await idFunc(this.numberValue)
            case DataType.Path:
                if (this.stringValue === null) return await nullFunc()
                return await pathFunc(this.stringValue)
            case DataType.String:
                if (this.stringValue === null) return await nullFunc()
                return await stringFunc(this.stringValue)
            case DataType.Integer:
                if (this.numberValue === null) return await nullFunc()
                return await integerFunc(this.numberValue)
            case DataType.Double:
                if (this.numberValue === null) return await nullFunc()
                return await doubleFunc(this.numberValue)
            case DataType.TimeStamp:
                if (this.timeStampValue === null) return await nullFunc()
                return await timeStampFunc(this.timeStampValue)
            case DataType.Folder:
                if (this.stringValue === null) return await nullFunc()
                return await folderFunc(this.stringValue)
            default:
                throw new StandardError("Invalid type passed to Variant.switchOnTypeWithNullAsync")
        }
    }

    public castAsID(): number | null {
        if (this.type !== DataType.ID) throw new StandardError("Tried to cast Variant to ID that wasn't an ID")

        return this.numberValue
    }

    public castAsIDNotNull(): number {
        const value = this.castAsID()
        if (value === null)
            throw new StandardError("Tried to cast Variant to ID that was null but was not allowed to be null")

        return value
    }

    public castAsPath(): string | null {
        if (this.type !== DataType.Path) throw new StandardError("Tried to cast Variant to Path that wasn't Path")

        return this.stringValue
    }

    public castAsPathNotNull(): string {
        const value = this.castAsPath()
        if (value === null)
            throw new StandardError("Tried to cast Variant to Path that was null but was not allowed to be null")

        return value
    }

    public castAsString(): string | null {
        if (this.type !== DataType.String) throw new StandardError("Tried to cast Variant to String that wasn't String")

        return this.stringValue
    }

    public castAsStringNotNull(): string {
        const value = this.castAsString()
        if (value === null)
            throw new StandardError("Tried to cast Variant to String that was null but was not allowed to be null")

        return value
    }

    public castAsInteger(): number | null {
        if (this.type !== DataType.Integer)
            throw new StandardError("Tried to cast Variant to Integer that wasn't Integer")

        return this.numberValue
    }

    public castAsIntegerNotNull(): number {
        const value = this.castAsInteger()
        if (value === null)
            throw new StandardError("Tried to cast Variant to Integer that was null but was not allowed to be null")

        return value
    }

    public castAsDouble(): number | null {
        if (this.type !== DataType.Double) throw new StandardError("Tried to cast Variant to Double that wasn't Double")

        return this.numberValue
    }

    public castAsDoubleNotNull(): number {
        const value = this.castAsDouble()
        if (value === null)
            throw new StandardError("Tried to cast Variant to Double that was null but was not allowed to be null")

        return value
    }

    public castAsTimeStamp(): TimeStamp | null {
        if (this.type !== DataType.TimeStamp)
            throw new StandardError("Tried to cast Variant to TimeStamp that wasn't TimeStamp")

        return this.timeStampValue
    }

    public castAsBinary(): BinaryRep | null {
        if (this.type !== DataType.Binary) throw new StandardError("Tried to cast Variant to Binary that wasn't Binary")

        if (this.binaryValue === null) return null
        return BinaryRep.fromJSON(this.binaryValue)
    }

    public castAsBinaryNotNull(): BinaryRep {
        const value = this.castAsBinary()
        if (value === null)
            throw new StandardError("Tried to cast Variant to Binary that was null but was not allowed to be null")

        return value
    }

    public convertToString(): string {
        return this.switchOnTypeWithNull<string>(
            () => {
                return "(null)"
            },
            id => {
                return id.toString()
            },
            path => {
                return path
            },
            string => {
                return string
            },
            integer => {
                return integer.toString()
            },
            double => {
                return double.toString()
            },
            timeStamp => {
                return timeStamp.toUTCString()
            },
            folder => {
                return folder
            },
            binary => {
                return binary.toString()
            },
        )
    }

    public setFromString(value: string): void {
        this.switchOnType<void>(
            id => {
                this.numberValue = parseInt(value)
            },
            path => {
                this.stringValue = value
            },
            string => {
                this.stringValue = value
            },
            integer => {
                this.numberValue = parseInt(value)
            },
            double => {
                this.numberValue = parseFloat(value)
            },
            timeStamp => {
                this.timeStampValue = TimeStamp.fromDateObjectSecondsOnly(new Date(value))
            },
            folder => {
                this.stringValue = value
            },
            binary => {
                this.binaryValue = value
            },
        )
    }

    public toClasslessVariant(): VariantClassless {
        let v: unknown = null

        switch (this.type) {
            case DataType.ID:
                v = this.numberValue
                break
            case DataType.Path:
                v = this.stringValue
                break
            case DataType.String:
                v = this.stringValue
                break
            case DataType.Integer:
                v = this.numberValue
                break
            case DataType.Double:
                v = this.numberValue
                break
            case DataType.TimeStamp:
                if (this.timeStampValue === null) v = null
                else v = this.timeStampValue.toNativeTimeStamp()
                break
            case DataType.Binary:
                if (this.binaryValue === null) v = null
                else v = this.binaryValue
                break
            default:
                throw new StandardError("Unknown type in toClasslessVariant()")
        }

        return { t: this.type, v }
    }

    static fromClasslessVariant(variant: VariantClassless): Variant {
        if (
            variant.v === null ||
            typeof variant.v === "string" ||
            typeof variant.v === "number" ||
            variant.v instanceof TimeStamp
        ) {
            return new Variant(variant.t, variant.v)
        }

        throw new StandardError("Unknown type passed to fromClasslessVariant()")
    }
}
export interface VariantClassless {
    t: DataType // Type
    v: unknown // Value
}
export function convertResultRowVariantToResultRowVariantClassless(
    resultRow: ResultRowVariant,
): ResultRowVariantClassless {
    const classlessResultRow: ResultRowVariantClassless = {}

    for (const property of Object.keys(resultRow)) {
        classlessResultRow[property] = resultRow[property].toClasslessVariant()
    }

    return classlessResultRow
}

export function convertResultRowVariantClasslessToResultRowVariant(
    classlessResultRow: ResultRowVariantClassless,
): ResultRowVariant {
    const resultRow: ResultRowVariant = {}

    for (const property of Object.keys(classlessResultRow)) {
        resultRow[property] = Variant.fromClasslessVariant(classlessResultRow[property])
    }

    return resultRow
}
